import {
  PropsWithChildren,
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  descriptionContainer,
  slideButtonIcon,
  slideButtonsContainer,
  slideContainer,
  slideImageContainer,
  slideImagesContainer,
  slideInfoContainer,
  slideshowContainer,
} from "./styles.css";
import { AccentText } from "../../typography";
import { ChevronLeftIcon } from "../../../../shared/components/icons/ChevronLeftIcon";
import { ButtonIcon } from "../../../../shared/components/link/ButtonIcon";
import { ChevronRightIcon } from "../../../../shared/components/icons/ChevronRightIcon";
import { useInView, motion, AnimatePresence } from "motion/react";

type Slide = {
  image: ReactNode;
  title: string;
  description: string;
};

type SlideshowProps = {
  slides: Slide[];
};

type ImageContainerProps = {
  onInView?: () => void;
  parentRef: RefObject<HTMLDivElement>;
};

const ImageContainer = ({
  children,
  onInView,
  parentRef,
}: PropsWithChildren<ImageContainerProps>) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const isInView = useInView(containerRef, { amount: 0.6, root: parentRef });

  useEffect(() => {
    isInView && onInView?.();
  }, [isInView]);

  return (
    <div ref={containerRef} className={slideImageContainer}>
      {children}
    </div>
  );
};

export const Slideshow = ({ slides }: SlideshowProps) => {
  const [index, setIndex] = useState(0);
  const imagesRef = useRef<HTMLDivElement>(null);

  const slidesCount = slides.length;

  const scrollToImage = (index: number) => {
    imagesRef.current?.scrollTo({
      left: index * imagesRef.current.clientWidth,
    });
  };

  const nextSlide = () => {
    if (index === slidesCount - 1) {
      return;
    }

    const nextIndex = (index + 1) % slidesCount;
    scrollToImage(nextIndex);
    setIndex(nextIndex);
  };

  const prevSlide = () => {
    if (index === 0) {
      return;
    }

    const prevIndex = (index - 1 + slidesCount) % slidesCount;
    scrollToImage(prevIndex);
    setIndex(prevIndex);
  };

  const { description, title } = slides[index];

  return (
    <div className={slideshowContainer}>
      <div className={slideContainer}>
        <div ref={imagesRef} className={slideImagesContainer}>
          {slides.map(({ image, title }, _index) => (
            <ImageContainer
              parentRef={imagesRef}
              key={title}
              onInView={() => setIndex(_index)}
            >
              {image}
            </ImageContainer>
          ))}
        </div>
        <div className={slideButtonsContainer}>
          <ButtonIcon primary onClick={prevSlide} disabled={index === 0}>
            <ChevronLeftIcon className={slideButtonIcon} />
          </ButtonIcon>
          <span>{`${index + 1}/${slidesCount}`}</span>
          <ButtonIcon primary onClick={nextSlide} disabled={index === slidesCount - 1}>
            <ChevronRightIcon className={slideButtonIcon} />
          </ButtonIcon>
        </div>
      </div>
      <motion.div className={slideInfoContainer}>
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            className={descriptionContainer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <p>
              <AccentText>{title}</AccentText>
            </p>
            <p>{description}</p>
          </motion.div>
        </AnimatePresence>
        {slides.map(({ title, description }, _index) => (
          <div
            key={title}
            className={descriptionContainer}
            style={{ visibility: "hidden" }} // only for the parent height calculation
          >
            <p>
              <AccentText>{title}</AccentText>
            </p>
            <p>{description}</p>
          </div>
        ))}
      </motion.div>
    </div>
  );
};
